import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";

@customElement("edit-plm-product-dialog")
export class EditPlmProductDialog extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    .form-field {
      margin-bottom: var(--spacing-16, 16px);
    }

    .form-field label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      margin-bottom: var(--spacing-4, 4px);
    }

    .form-field .help {
      font-size: 11px;
      color: var(--colors-foreground-fg-gray-500, #98a2b3);
      margin-top: var(--spacing-4, 4px);
    }

    .read-only {
      font-family: monospace;
      font-size: 13px;
      color: var(--colors-foreground-fg-gray-900, #1d2433);
    }

    .modal-footer {
      display: flex;
      gap: var(--spacing-8, 8px);
      justify-content: flex-end;
    }

    bee-alert {
      margin-bottom: var(--spacing-16, 16px);
    }
  `;

  @property({ type: Boolean }) open = false;
  @property() tenant = "";
  @property() instance = "";
  @property() name = "";
  @property() hostname = "";
  @property({ attribute: false }) versions: string[] = [];

  @state() private _hostname = "";
  @state() private _versions = "";
  @state() private _applying = false;
  @state() private _error = "";

  // Seed the editable fields from the incoming product whenever the dialog opens.
  willUpdate(changed: Map<string, unknown>) {
    // Re-seed on open, and also when the product identity changes while already
    // open (e.g. switching which product is edited without closing first).
    const opened = changed.has("open") && this.open;
    const identityChanged = this.open && (changed.has("tenant") || changed.has("instance") || changed.has("name"));
    if (opened || identityChanged) {
      this._hostname = this.hostname ?? "";
      this._versions = (this.versions ?? []).join(", ");
      this._error = "";
      this._applying = false;
    }
  }

  private _close() {
    this.dispatchEvent(new CustomEvent("close"));
  }

  private async _submit() {
    if (this._applying) return;
    this._applying = true;
    this._error = "";
    try {
      const versions = this._versions
        ? this._versions.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
      const { product } = await api.updatePlmProduct({
        tenant: this.tenant,
        instance: this.instance,
        name: this.name,
        hostname: this._hostname.trim(),
        versions,
      });
      this.dispatchEvent(new CustomEvent("plm-product-updated", { detail: product }));
      this._close();
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to update PLM product";
    } finally {
      this._applying = false;
    }
  }

  render() {
    if (!this.open) return nothing;

    return html`
      <bee-modal open in-root title="Edit PLM Product — ${this.tenant} / ${this.instance}" @bee-request-close=${this._close}>
        <div class="form-field">
          <label>Product Name</label>
          <div class="read-only">${this.name}</div>
          <div class="help">The name is the record key and cannot be changed.</div>
        </div>

        <div class="form-field">
          <label>Hostname</label>
          <bee-input
            placeholder="e.g., platform.acme.com"
            .value=${this._hostname}
            @bee-input=${(e: Event) => { this._hostname = (e.target as HTMLInputElement).value.trim(); }}
          ></bee-input>
        </div>

        <div class="form-field">
          <label>Versions</label>
          <bee-input
            placeholder="e.g., 14.3.0.1, 14.3.0.2"
            .value=${this._versions}
            @bee-input=${(e: Event) => { this._versions = (e.target as HTMLInputElement).value; }}
          ></bee-input>
          <div class="help">Comma-separated version strings.</div>
        </div>

        ${this._error ? html`<bee-alert variant="error" open><span slot="message">${this._error}</span></bee-alert>` : nothing}

        <div slot="footer" class="modal-footer">
          <bee-button label="Cancel" bee-type="secondary-grey" @click=${this._close} ?disabled=${this._applying}></bee-button>
          <bee-button
            label=${this._applying ? "Saving..." : "Save Changes"}
            bee-type="primary"
            @click=${this._submit}
            ?disabled=${this._applying}
            ?loading=${this._applying}
          ></bee-button>
        </div>
      </bee-modal>
    `;
  }
}
